/**
 * Actions - Earned (Off-page) Page (Spec 045)
 *
 * Shows all earned media actions across all playbooks for the selected brand.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { apiGet, apiFetch } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { Spinner } from '../components/ui/Icon.js';
import { Select } from '../components/Select.js';
import { useFullBleed } from '../lib/layout-width.js';

interface ActionItem {
  id: string;
  action_type: string;
  title: string;
  description: string | null;
  target_urls: string[] | null;
  effort_estimate: string | null;
  status: string;
  completed_at: string | null;
  notes: string | null;
  playbook_id: string;
  playbook_title: string;
}

interface ActionsResponse {
  data: ActionItem[];
  total: number;
}

const STATUS_COLORS: Record<string, string> = {
  todo: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  done: 'bg-green-100 text-green-700',
  skipped: 'bg-yellow-100 text-yellow-700',
};

const ACTION_STATUSES = ['todo', 'in_progress', 'done', 'skipped'] as const;

export function ActionsEarnedPage() {
  useFullBleed();
  const { selectedBrand, user } = useBrand();
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchActions = useCallback(async () => {
    if (!selectedBrand || !user) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ category: 'earned', limit: '100' });
      if (statusFilter) params.set('status', statusFilter);
      const result = await apiGet<ActionsResponse>(
        `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/actions?${params.toString()}`
      );
      setActions(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user, statusFilter]);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  const updateStatus = async (playbookId: string, actionId: string, status: string) => {
    try {
      await apiFetch(`/api/v1/playbooks/${playbookId}/actions/${actionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      fetchActions();
    } catch { /* ignore */ }
  };

  if (!selectedBrand) {
    return (
      <div className="hub-page--table" data-density="compact">
        <h1 className="hub-page__title">Earned actions</h1>
        <EmptyState icon="bx-book-open" title="Select a brand" description="Choose a brand to view earned actions." />
      </div>
    );
  }

  return (
    <div data-testid="actions-earned-page" className="hub-page--table" data-density="compact">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Earned actions (off-page)</h1>
          <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">All earned media actions across playbooks for {selectedBrand.name}</p>
        </div>
      </div>

      <div className="hub-toolbar">
        <Select
          options={[
            { value: '', label: 'All statuses' },
            ...ACTION_STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') })),
          ]}
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="All statuses"
          className="w-44"
        />
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="flex justify-center py-8"><Spinner className="text-[var(--hub-color-action-text)]" /></div>}

      {!loading && actions.length === 0 && (
        <EmptyState
          icon="bx-book-open"
          title="No earned actions yet"
          description="Generate a playbook to create actionable items."
          variant="onboarding"
          actionLabel="Go to playbooks"
          actionHref="/playbooks"
        />
      )}

      {!loading && actions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="hub-table" data-testid="earned-actions-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Type</th>
                <th>Playbook</th>
                <th>Effort</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((action) => (
                <tr key={action.id}>
                  <td>
                    <div className="font-medium">{action.title}</div>
                    {action.description && <div className="mt-0.5 text-xs text-[var(--hub-color-ink-muted)] line-clamp-1">{action.description}</div>}
                  </td>
                  <td>
                    <span className="hub-chip hub-chip--info">{action.action_type}</span>
                  </td>
                  <td>
                    <Link to={`/playbooks/${action.playbook_id}`} className="text-[var(--hub-color-action-text)]">{action.playbook_title}</Link>
                  </td>
                  <td>{action.effort_estimate ?? '--'}</td>
                  <td>
                    <Select
                      options={ACTION_STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
                      value={action.status}
                      onChange={(v) => updateStatus(action.playbook_id, action.id, v)}
                      className="w-32"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
